import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';

import type {
  IReports,
  IReqCreateReport,
  IResReportsError,
  IResReportsOK,
} from '@/types/parcel/orders/reports.interface';

interface IReportsState {
  data: IReports[];
  total: number;
  loading: boolean;
  error: IResReportsError | null | unknown;
}

const hasReportsInProcess = (reportsList: IReports[]) => {
  return reportsList.some(({ status }) => status === 'CREATED' || status === 'PROCESSING');
};

const checkForCompletedReports = (newReports: IReports[], previousReports: IReports[]) => {
  for (const prev of previousReports) {
    if (prev.status === 'CREATED' || prev.status === 'PROCESSING') {
      const updated = newReports.find((r) => r.id === prev.id && r.status === 'PROCESSED');
      if (updated) toast.success('¡Reporte generado exitosamente!');
    }
  }
};

const useMyReportsSection = () => {
  const pagination = useServerPagination();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousReportsRef = useRef<IReports[]>([]);

  const [openReportsModal, setOpenReportsModal] = useState(false);

  const [reports, setReports] = useState<IReportsState>({
    data: [],
    loading: false,
    error: null,
    total: 0,
  });

  const [reportFields, setReportFields] = useState<IReqCreateReport>({
    betweenDates: { start: '', finish: '' },
    note: '',
    status: [],
  });

  const clearPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const fetchReports = useCallback(async () => {
    const http = initializeHttpProvider();
    try {
      const res = await http.orders.reports({
        page: pagination.filters.page ? pagination.filters.page + 1 : 1,
        pageSize: pagination.filters.page_size,
      });
      return (res as IResReportsOK).data;
    } catch (err) {
      console.error('Error al obtener reportes', err);
      throw err;
    }
  }, [pagination.filters.page, pagination.filters.page_size]);

  const pollReports = useCallback(() => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(async () => {
      try {
        const data = await fetchReports();

        checkForCompletedReports(data.reports, previousReportsRef.current);
        previousReportsRef.current = data.reports;

        setReports({ data: data.reports, loading: false, error: null, total: data.total });

        if (!hasReportsInProcess(data.reports)) clearPolling();
      } catch (err) {
        console.error('Error en polling de reportes', err);
      }
    }, 10_000);
  }, [fetchReports]);

  const getReports = useCallback(async () => {
    setReports((prev) => ({ ...prev, loading: true }));
    try {
      const data = await fetchReports();

      checkForCompletedReports(data.reports, previousReportsRef.current);
      previousReportsRef.current = data.reports;

      setReports({ data: data.reports, loading: false, error: null, total: data.total });

      if (hasReportsInProcess(data.reports)) pollReports();
      else clearPolling();
    } catch (err) {
      setReports((prev) => ({ ...prev, loading: false, error: err }));
    }
  }, [fetchReports, pollReports]);

  const createReport = useCallback(
    async (fields: IReqCreateReport) => {
      const http = initializeHttpProvider();
      try {
        await http.orders.createReport(fields);
        toast.info('Nuevo reporte solicitado. Se procesará en breve.');
        getReports();
      } catch (err) {
        console.error('Error al crear reporte', err);
        toast.error('Error al crear el reporte. Intenta nuevamente.');
      }
    },
    [getReports],
  );

  const openCloseReportsModal = () => setOpenReportsModal((prev) => !prev);

  const openCloseReportsWithRefresh = () => {
    setOpenReportsModal(false);
    getReports();
  };

  const handleReportFields = (field: keyof IReqCreateReport, value: string | string[]) => {
    setReportFields((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    getReports();
  }, [getReports]);

  useEffect(() => clearPolling, []);

  return {
    loading: reports.loading,
    reports: reports.data,
    total: reports.total,
    pagination,
    createReport,
    openReportsModal,
    openCloseReportsModal,
    openCloseReportsWithRefresh,
    handleReportFields,
    reportFields,
  };
};

export default useMyReportsSection;
